import React, { useState } from 'react'
import clsx from 'clsx'

import {
    Chip,
    Grid,
    Button,
    Theme,
    Tooltip,
    Typography,
} from '@material-ui/core'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'
import LinkOffIcon from '@material-ui/icons/LinkOff'

import {
    AreaConfig,
    AreaStatusTag,
    getServiceImageDetails,
    ListVendor,
    ModelMap,
    TemplateService,
    TemplateWorkorder,
    Folder,
    UnitConfig,
} from '../../../../models'

import { TemplateVendorRow } from './TemplateVendorRow'
import { ServiceSpecifyUnitsModal } from './ServiceSpecifyUnitsModal'
import { VENDOR_BAR_H } from '../../constants'
import { useTemplateScheduleGanttStyles } from '../../styles'

import { setEndOfDay, setMidnight } from '../../../../helpers'
import { UpdateTemplateServiceVendorParams } from '../../../../hooks/useEzSchedule/types'
import { typographyStyle } from '../../../../theme/typographyStyle'

interface Props {
    theme: Theme
    templateService: TemplateService
    vendorList: ListVendor[]
    projectDuration: number
    lastRow?: boolean
    infrastructure: Folder
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    useOrderControl?: boolean
    isFirstService?: boolean
    workOrderCount?: number
    templateWorkorders?: TemplateWorkorder[]
}

export const TemplateServiceDetailRow = (props: Props) => {
    const {
        theme,
        templateService,
        infrastructure,
        unitConfigMap,
        areaConfigMap,
        areaStatusTagMap,
        updateTemplateServiceVendor,
        useOrderControl = false,
        isFirstService = true,
        workOrderCount = 0,
    } = props
    const classes = useTemplateScheduleGanttStyles()
    const [specifyUnitsOpen, setSpecifyUnitsOpen] = useState(false)

    const showDisabledOrderControl = useOrderControl && !isFirstService

    if (showDisabledOrderControl) {
        const rowHeight = Math.max(
            (templateService.template_service_vendors.length + 1) *
                VENDOR_BAR_H,
            VENDOR_BAR_H,
        )
        const disabledVendorRows = templateService.template_service_vendors.map(
            (tsv) => (
                <TemplateVendorRow
                    key={`TEMPLATE-VENDOR-${tsv.id}`}
                    {...props}
                    templateVendor={tsv}
                    infrastructure={infrastructure}
                />
            ),
        )
        return (
            <Grid
                container
                item
                xs={12}
                className={clsx(
                    classes.serviceDetailRowContainer,
                    classes.orderControlDisabledRow,
                    !props.lastRow &&
                        classes.serviceDetailRowContainerWithBorder,
                )}
                style={{
                    height: rowHeight,
                    overflow: 'hidden',
                    background: templateService.stand_alone
                        ? theme.palette.action.hover
                        : undefined,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    {/* Service header row */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                            width: '100%',
                            height: VENDOR_BAR_H,
                            minHeight: VENDOR_BAR_H,
                            boxSizing: 'border-box',
                            padding: theme.spacing(0, 1),
                            flexShrink: 0,
                        }}
                    >
                        <img
                            src={
                                getServiceImageDetails(
                                    templateService.service.id,
                                    templateService.service.service_category,
                                ).icon
                            }
                            style={{
                                maxHeight: 40,
                                maxWidth: 40,
                                opacity: 0.7,
                            }}
                            alt=""
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight:
                                        theme.typography.fontWeightMedium,
                                    color: theme.palette.text.secondary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={templateService.service.name}
                            >
                                {templateService.service.name}
                            </Typography>
                        </div>
                        {templateService.stand_alone && (
                            <Chip
                                label="Stand Alone"
                                size="small"
                                variant="outlined"
                                icon={<LinkOffIcon style={{ fontSize: 14 }} />}
                            />
                        )}
                    </div>
                    {/* Vendor rows */}
                    {disabledVendorRows}
                </div>
            </Grid>
        )
    }

    const containerHeight =
        (templateService.template_service_vendors.length + 1) * VENDOR_BAR_H

    const showSpecifyUnits = templateService.template_service_vendors.length > 1

    const vendorRows = templateService.template_service_vendors.map((tsv) => {
        const startDate = new Date(tsv.start_date)
        const endDate = new Date(tsv.end_date)
        setMidnight(startDate)
        setEndOfDay(endDate)

        return (
            <TemplateVendorRow
                key={`TEMPLATE-VENDOR-${tsv.id}`}
                {...props}
                templateVendor={tsv}
                infrastructure={infrastructure}
            />
        )
    })

    return (
        <Grid
            container
            item
            xs={12}
            className={clsx(
                classes.serviceDetailRowContainer,
                !props.lastRow && classes.serviceDetailRowContainerWithBorder,
            )}
            style={{
                height: containerHeight,
                background: templateService.stand_alone
                    ? theme.palette.action.hover
                    : undefined,
            }}
        >
            {/* Top Row: Service Name + Assign Units */}
            <Grid
                xs={12}
                container
                item
                className={classes.serviceDetailRowHeader}
            >
                <Grid xs={2}>
                    <img
                        src={
                            getServiceImageDetails(
                                templateService.service.id,
                                templateService.service.service_category,
                            ).icon
                        }
                        style={{
                            maxHeight: 40,
                            maxWidth: 40,
                        }}
                    />
                </Grid>
                <Grid
                    xs={showSpecifyUnits ? 6 : 10}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        minWidth: 0,
                    }}
                >
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h5),
                            marginLeft: theme.spacing(1),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                        }}
                        title={templateService.service.name}
                    >
                        {templateService.service.name}
                    </span>
                    {templateService.stand_alone && (
                        <Chip
                            label="Stand Alone"
                            size="small"
                            variant="outlined"
                            icon={<LinkOffIcon style={{ fontSize: 14 }} />}
                            style={{
                                marginLeft: theme.spacing(1),
                                marginTop: theme.spacing(0.5),
                                alignSelf: 'flex-start',
                            }}
                        />
                    )}
                </Grid>
                {showSpecifyUnits && (
                    <Grid xs={4} className={classes.assignUnitsButtonContainer}>
                        <Tooltip title="Assign units per vendor">
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                startIcon={<MeetingRoomIcon fontSize="small" />}
                                onClick={() => setSpecifyUnitsOpen(true)}
                                className={classes.assignUnitsButton}
                                aria-label={`Assign units for ${templateService.service.name}`}
                            >
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        lineHeight: 1.05,
                                    }}
                                >
                                    <span style={{ display: 'block' }}>
                                        Assign
                                    </span>
                                    <span style={{ display: 'block' }}>
                                        Units
                                    </span>
                                </span>
                            </Button>
                        </Tooltip>
                    </Grid>
                )}
            </Grid>

            {/* Vendor Rows */}
            {vendorRows}
            <ServiceSpecifyUnitsModal
                open={specifyUnitsOpen}
                onClose={() => setSpecifyUnitsOpen(false)}
                infrastructure={infrastructure}
                templateService={templateService}
                updateTemplateServiceVendor={updateTemplateServiceVendor}
                unitConfigMap={unitConfigMap}
                areaConfigMap={areaConfigMap}
                areaStatusTagMap={areaStatusTagMap}
            />
        </Grid>
    )
}
